// SB6-ANA-PROG-02 — filter-revision tagging + per-widget sync derivation.
//
// The Global Data Point Filter store carries a monotonic `revision`
// (bumped on every selection change and every committed AI-config change —
// see `state/dataPointFilterStore.ts`). This module threads that revision
// THROUGH TanStack Query so each widget can tell whether the data it is
// currently showing answers the *latest* filter, or is a stale
// `keepPreviousData` placeholder from an earlier revision.
//
// How the tag is threaded:
//   * At fetch time the query hooks read the live store revision
//     (`useDataPointFilterStore.getState().revision`) and stamp it onto the
//     resolved payload via `tagRevision`. The tag is stored under a Symbol
//     key so it never appears in JSON, never collides with a wire field,
//     and never disturbs typed consumers (`data.items`, `data.total`, …).
//   * A widget derives its sync state with `useWidgetSyncState`, which
//     compares the *displayed* payload's tagged revision against the live
//     store revision. Out-of-sync iff `displayedRevision !== latest` OR the
//     query is fetching. The revision-mismatch arm is what makes a
//     queued-but-not-yet-fired refetch count as out-of-sync (the store
//     revision moves ahead the instant the user clicks, before
//     `isFetching` flips), which `isFetching` alone cannot capture.
import { useIsFetching } from "@tanstack/react-query";
import { useDataPointFilterStore } from "./state/dataPointFilterStore.js";

/** Symbol key under which the answered revision is stamped on a payload. */
const REVISION_TAG = Symbol.for("wp.filterRevision");

/**
 * Stamp the revision a payload answered onto the payload object (under a
 * non-enumerable Symbol key) and return it. Non-object payloads (null /
 * primitives) are returned untouched — the tag reader treats an absent tag
 * as "revision 0" / always-stale-until-first-load, which is the correct
 * default.
 */
export function tagRevision<T>(payload: T, revision: number): T {
  if (payload !== null && typeof payload === "object") {
    Object.defineProperty(payload, REVISION_TAG, {
      value: revision,
      enumerable: false,
      configurable: true,
      writable: true,
    });
  }
  return payload;
}

/** Read the revision stamped on a payload, or `null` when untagged. */
export function readRevisionTag(payload: unknown): number | null {
  if (payload !== null && typeof payload === "object") {
    const tagged = payload as Record<symbol, unknown>;
    const v = tagged[REVISION_TAG];
    if (typeof v === "number") return v;
  }
  return null;
}

/**
 * Read the live latest filter revision from the store. Use inside a
 * `queryFn` (non-reactive) to stamp the payload with the revision in
 * effect at fetch time.
 */
export function currentRevision(): number {
  return useDataPointFilterStore.getState().revision;
}

export interface WidgetSyncState {
  /** True while the widget's displayed data does not answer the latest filter. */
  outOfSync: boolean;
  /** The lowest (most stale) revision the displayed payloads answered. */
  displayedRevision: number | null;
  /** The latest filter revision. */
  latestRevision: number;
}

/** One dependent query's sync-relevant flags. */
export interface WidgetSyncInput {
  /** The displayed payload (used to read its answered-revision tag). */
  data: unknown;
  /** TanStack `isFetching` — a refetch is in flight for this query. */
  isFetching: boolean;
  /**
   * TanStack `isPlaceholderData` — the displayed payload is a
   * `keepPreviousData` placeholder from the previous query key (the new
   * key's data has not landed yet). Precisely catches the
   * keepPreviousData refetch window, including the synchronous render
   * right after a filter click, before `isFetching` may have flipped.
   * Omit (default false) for queries that don't use `keepPreviousData`.
   */
  isPlaceholderData?: boolean;
  /**
   * When true, additionally mark this input out-of-sync if its displayed
   * payload's revision tag is behind the latest revision. Use ONLY for
   * queries whose key encodes the FULL global selection (the data-points
   * query) — there a stale tag always means a pending refetch the user
   * triggered. Do NOT use for L1+L2-scoped facet queries (defect /
   * ai-class), whose key legitimately stays put when an unrelated lower
   * axis changes; for those, `isPlaceholderData` is the exact signal.
   */
  checkRevision?: boolean;
}

/**
 * Derive a widget's sync state from one (or more) dependent query results.
 *
 * Per input, out-of-sync when ANY of:
 *   * `isFetching` (a refetch is in flight), OR
 *   * `isPlaceholderData` (showing a keepPreviousData placeholder while the
 *     new key's data is in flight — catches the queued-but-not-yet-fired
 *     window the moment the key changes), OR
 *   * `checkRevision` is set and the displayed payload's revision tag is
 *     behind the latest (belt-and-suspenders for the full-selection
 *     data-points query, which carries no placeholder).
 *
 * The aggregate widget state is out-of-sync iff ANY input is.
 */
export function useWidgetSyncState(
  inputs: readonly WidgetSyncInput[],
): WidgetSyncState {
  const latestRevision = useDataPointFilterStore((s) => s.revision);

  let outOfSync = false;
  let displayedRevision: number | null = null;
  for (const input of inputs) {
    const rev = readRevisionTag(input.data);
    if (
      rev !== null &&
      (displayedRevision === null || rev < displayedRevision)
    ) {
      displayedRevision = rev;
    }
    if (input.isFetching) outOfSync = true;
    if (input.isPlaceholderData) outOfSync = true;
    if (input.checkRevision && rev !== null && rev !== latestRevision) {
      outOfSync = true;
    }
  }

  return { outOfSync, displayedRevision, latestRevision };
}

/**
 * Aggregate "is the filter fully applied?" signal for the FilterWidget
 * header ("applying…" vs. a quiet "applied"). Derived from the revision
 * model for free: the dependent set is in sync iff NO query under the
 * shared `["analysis", "data-points", <id>]` key prefix is fetching (that
 * prefix covers the data-points list + the facets + the defect-type and
 * ai-class facet queries — every widget reduces from one of these).
 *
 * The data-points-key prefix is inlined here to avoid an import cycle with
 * `queries.ts` (which imports the tagging helpers from this module). It
 * MUST stay in step with `analysisKeys.dataPoints(id)`.
 */
export function useFilterApplyState(inspectionId: string): {
  applying: boolean;
} {
  // Subscribe to the revision so the aggregate re-derives the instant a
  // filter click bumps it (before any refetch has a chance to start).
  useDataPointFilterStore((s) => s.revision);
  const fetching = useIsFetching({
    queryKey: ["analysis", "data-points", inspectionId],
  });
  return { applying: fetching > 0 };
}
